import React from "react";
import { Stack, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch } from "../redux/store";
import { addSupplierContact } from "../redux/actions/supplierActions";
import { cleanFormData } from "../helpers/formHelpers";
import { SupplierContactRequestDto } from "../shared/dtos/supplierContact/SupplierContactRequestDto";

interface AddSupplierContactFormProps {
  supplierId: string;
  loading: boolean;
  onSuccess: () => void;
}

const schema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  phone: Yup.string().required("Phone is required"),
  email: Yup.string().email("Invalid email").nullable(),
  jobTitle: Yup.string().nullable(),
});

const AddSupplierContactForm: React.FC<AddSupplierContactFormProps> = ({
  supplierId,
  loading = false,
  onSuccess,
}): React.JSX.Element => {
  const dispatch = useAppDispatch();

  const initialValues: Partial<SupplierContactRequestDto> = {
    firstName: "",
    lastName: "",
    jobTitle: "",
    phone: "",
    email: "",
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={async (values) => {
          const payload = cleanFormData(values) as SupplierContactRequestDto;
          payload.supplierId = supplierId;
          await dispatch(addSupplierContact(payload));
          onSuccess();
        }}
      >
        {({
          dirty,
          isValid,
        }: FormikProps<Partial<SupplierContactRequestDto>>) => (
          <Form>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                }}
              >
                <FormTextField name="firstName" label="First Name" />
                <FormTextField name="lastName" label="Last Name" />
                <FormTextField
                  name="jobTitle"
                  label="Job Title (e.g. Sales Manager)"
                />
                <FormTextField name="phone" label="Direct Phone" />
                <Box sx={{ gridColumn: { xs: "1fr", sm: "span 2" } }}>
                  <FormTextField
                    name="email"
                    label="Email Address"
                    type="email"
                  />
                </Box>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <SubmitButton
                  title="Save Contact"
                  loading={loading}
                  disabled={!dirty || !isValid || loading}
                  startIcon={<AddCircleOutlineIcon />}
                  sx={{ width: "auto" }}
                />
              </Box>
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
export default AddSupplierContactForm;
